import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchAllowedMenus,
  fetchLocationData,
  creatProject,
} from "../Services/APIService";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useLocation } from "react-router-dom";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface ValidationErrors {
  projectName?: string;
  description?: string;
  locationID?: string;
}

interface Location {
  locationID: number;
  locationName: string;
}

const CreateProject: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [locations, setLocations] = useState<Location[]>([]);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    projectName: "",
    description: "",
    locationID: "",
    isActive: true,
  });
  const location = useLocation();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetchLocationData();
        if (!response) {
          throw new Error("Failed to fetch location data");
        }
        setLocations(response);
      } catch (error) {
        console.error("Error fetching location data:", error);
      }
    };
    fetchLocations();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    // Handle checkboxes
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked,
      });
      return;
    }

    // Prevent leading spaces for all inputs
    if (value.startsWith(" ")) {
      return;
    }

    // Update form data
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear errors when user types
    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // Required field validations
    if (!formData.projectName.trim()) {
      newErrors.projectName = "Project name is required";
      isValid = false;
    }

    if (!formData.locationID) {
      newErrors.locationID = "Location is required";
      isValid = false;
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    const userId = localStorage.getItem("UserId");
    const tenantId = localStorage.getItem("tenantId");

    const body = {
      projectName: formData.projectName,
      locationID: parseInt(formData.locationID),
      description: formData.description,
      tenantId: tenantId ? parseInt(tenantId) : 0,
      isActive: formData.isActive,
      createdBy: userId ? parseInt(userId) : 0,
      brochure: "",
      priceQuote: "",
    };

    try {
      const result = await creatProject(body);

      if (result.isCreated) {
        toast.success(result.message);
        setTimeout(() => {
          navigate("/projectView");
        }, 1000);
      } else {
        toast.error(result.message || "Project creation failed");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error occurred during project creation");
    }
  };

  // CSS for required field indicator
  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0"></div>
            <div className="card">
              <div className="card-header carHeaderWithBtn">
                <div className="card-title mb-0">Add New Project</div>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-12 col-lg-6 mb-3">
                      <label className="form-label">
                        Project Name{requiredField}
                      </label>
                      <input
                        name="projectName"
                        placeholder="Project Name"
                        value={formData.projectName}
                        onChange={handleChange}
                        required
                        className={`form-control form-control-lg ${
                          errors.projectName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.projectName && (
                        <div className="invalid-feedback text-danger">
                          {errors.projectName}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-lg-6 mb-3">
                      <label className="form-label">
                        Location{requiredField}
                      </label>
                      <select
                        name="locationID"
                        value={formData.locationID || ""}
                        onChange={handleChange}
                        required
                        className={`form-control form-control-lg ${
                          errors.locationID ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select Location</option>
                        {locations.map((location) => (
                          <option
                            key={location.locationID}
                            value={location.locationID}
                          >
                            {location.locationName}
                          </option>
                        ))}
                      </select>
                      {errors.locationID && (
                        <div className="invalid-feedback text-danger">
                          {errors.locationID}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-lg-6 mb-3">
                      <label className="form-label">
                        Description{requiredField}
                      </label>
                      <input
                        name="description"
                        placeholder="Description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        className={`form-control form-control-lg ${
                          errors.description ? "is-invalid" : ""
                        }`}
                      />
                      {errors.description && (
                        <div className="invalid-feedback text-danger">
                          {errors.description}
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-lg-4 mb-3 mt-3">
                      <label className="form-check">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleChange}
                          className="form-check-input"
                        />
                        <span className="form-check-label">Is Active</span>
                      </label>
                    </div>
                    <div className="col-12 col-lg-12">
                      <div>
                        <button type="submit" className="btn btn-primary me-2">
                          Submit
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => navigate("/projectView")}
                        >
                          <i className="bi bi-arrow-return-left"></i> Back To
                          List
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default CreateProject;
